import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavBar, Toast, Form, Field, Button, Selector, DatetimePicker, Popup, Cell, Picker } from 'react-vant';
import { ArrowLeft } from '@react-vant/icons';
import { getSessionDetail, updateSession, createSession, getClubs } from '../../../api/club';
import dayjs from 'dayjs';

const AdminSessionEdit = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNew = sessionId === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [clubs, setClubs] = useState([]);
  
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [showClubPicker, setShowClubPicker] = useState(false);
  const [clubName, setClubName] = useState('');

  useEffect(() => {
    fetchClubs();
    if (!isNew) {
      fetchDetail();
    }
  }, [sessionId]);

  const fetchClubs = async () => {
    const res = await getClubs({ page: 1, page_size: 200, status: 'active' });
    setClubs(res.items || []);
    return res.items || [];
  };

  const fetchDetail = async () => {
    try {
      const allClubs = await fetchClubs();
      const res = await getSessionDetail(sessionId);
      
      // 寻找社团名称
      const currentClub = allClubs.find(c => c.id === res.club_id);
      if (currentClub) setClubName(currentClub.name);
      else setClubName(res.club_name || '');

      form.setFieldsValue({
        ...res,
        registration_start: dayjs(res.registration_start).toDate(),
        registration_end: dayjs(res.registration_end).toDate(),
        max_capacity: String(res.max_capacity),
        round_number: String(res.round_number),
        club_id: res.club_id
      });
    } catch (error) {
      Toast.fail('获取详情失败');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      const data = {
        ...values,
        max_capacity: parseInt(values.max_capacity),
        round_number: parseInt(values.round_number),
        registration_start: dayjs(values.registration_start).toISOString(),
        registration_end: dayjs(values.registration_end).toISOString(),
        cancel_deadline: dayjs(values.registration_end).toISOString(), // 默认等于结束时间
        academic_year: 'all'
      };

      if (isNew) {
        await createSession(data);
        Toast.success('创建成功');
      } else {
        await updateSession(sessionId, data);
        Toast.success('更新成功');
      }
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      Toast.fail('操作失败');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <NavBar 
        title={isNew ? '发起新报名' : '编辑批次'} 
        leftText={<ArrowLeft size={20} />} 
        onClickLeft={() => navigate(-1)}
        fixed
        placeholder
      />

      <div style={{ padding: '16px' }}>
        <Form
          form={form}
          onFinish={onFinish}
          layout='vertical'
          footer={
            <div style={{ margin: '16px 0' }}>
              <Button nativeType='submit' type='primary' block round>
                保存批次
              </Button>
            </div>
          }
        >
          <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', padding: '0 12px' }}>
            <Form.Item
              label='关联社团'
              name='club_id'
              rules={[{ required: true, message: '请选择社团' }]}
              onClick={() => setShowClubPicker(true)}
            >
              <div style={{ padding: '10px 0', color: clubName ? '#0f172a' : '#94a3b8', fontSize: '14px' }}>
                {clubName || '点击选择社团'}
              </div>
            </Form.Item>

            <Form.Item
              label='报名轮次'
              name='round_number'
              initialValue="1"
            >
              <Field type="digit" placeholder='如：1' />
            </Form.Item>

            <Form.Item
              label='最大名额'
              name='max_capacity'
              initialValue="30"
              rules={[{ required: true, message: '请输入名额' }]}
            >
              <Field type="digit" placeholder='请输入名额' />
            </Form.Item>

            <Form.Item
              label='开始时间'
              name='registration_start'
              onClick={() => setShowStart(true)}
              rules={[{ required: true, message: '请选择开始时间' }]}
            >
              <div style={{ padding: '10px 0', color: form.getFieldValue('registration_start') ? '#0f172a' : '#94a3b8', fontSize: '14px' }}>
                {form.getFieldValue('registration_start') ? dayjs(form.getFieldValue('registration_start')).format('YYYY-MM-DD HH:mm') : '请选择开始时间'}
              </div>
            </Form.Item>

            <Form.Item
              label='结束时间'
              name='registration_end'
              onClick={() => setShowEnd(true)}
              rules={[{ required: true, message: '请选择结束时间' }]}
            >
              <div style={{ padding: '10px 0', color: form.getFieldValue('registration_end') ? '#0f172a' : '#94a3b8', fontSize: '14px' }}>
                {form.getFieldValue('registration_end') ? dayjs(form.getFieldValue('registration_end')).format('YYYY-MM-DD HH:mm') : '请选择结束时间'}
              </div>
            </Form.Item>

            <Form.Item
              label='状态'
              name='status'
              initialValue='upcoming'
            >
              <Selector
                options={[
                  { label: '未开始', value: 'upcoming' },
                  { label: '报名中', value: 'open' },
                  { label: '活动中', value: 'ongoing' },
                  { label: '已结束', value: 'closed' },
                ]}
                style={{ '--rv-selector-border-radius': '8px' }}
              />
            </Form.Item>
          </div>
        </Form>
      </div>

      {/* 弹窗部分 */}
      <Popup visible={showClubPicker} round position='bottom' onClose={() => setShowClubPicker(false)}>
        <Picker
          title='选择社团'
          columns={clubs.map(c => ({ text: c.name, value: c.id }))}
          onCancel={() => setShowClubPicker(false)}
          onConfirm={(val, option) => {
            form.setFieldsValue({ club_id: val });
            setClubName(option.text);
            setShowClubPicker(false);
          }}
        />
      </Popup>

      <Popup visible={showStart} round position='bottom' onClose={() => setShowStart(false)}>
        <DatetimePicker
          type='datetime'
          title='选择开始时间'
          minDate={new Date(2025, 0, 1)}
          onCancel={() => setShowStart(false)}
          onConfirm={(val) => {
            form.setFieldsValue({ registration_start: val });
            setShowStart(false);
          }}
        />
      </Popup>

      <Popup visible={showEnd} round position='bottom' onClose={() => setShowEnd(false)}>
        <DatetimePicker
          type='datetime'
          title='选择结束时间'
          minDate={new Date(2025, 0, 1)}
          onCancel={() => setShowEnd(false)}
          onConfirm={(val) => {
            form.setFieldsValue({ registration_end: val });
            setShowEnd(false);
          }}
        />
      </Popup>
    </div>
  );
};

export default AdminSessionEdit;
